import { useEffect, useRef, useState } from "react";
import { tryApiGet } from "../api/client";
import {
  GROUP_INFO,
  activeField,
  byteRows,
  captionText,
  fieldSummary,
  groupsPresent,
  hopNote,
  rangeText,
  togglePinned,
  type PacketBytesResponse,
} from "../lib/packetBytes";

// The packet's raw bytes, 16 to a row, each field tinted by what kind of field it is. The server cuts
// the packet into fields (GET /api/packets/{hash}/bytes, computed from the stored capture); colour is
// never the only cue: every field has a name and value on hover (or tap), a row in the key below, and
// each group has its own line style under the byte (see the pb-g-* rules in 09-payload-mqtt-ask.css).
export function PacketBytesSection({ hash, routeHops }: { hash: string; routeHops: number }) {
  const [data, setData] = useState<PacketBytesResponse | null | "unavailable">(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    tryApiGet<PacketBytesResponse>(`/api/packets/${encodeURIComponent(hash)}/bytes`)
      .then((res) => !cancelled && setData(res ?? "unavailable"))
      .catch(() => !cancelled && setData("unavailable"));
    return () => {
      cancelled = true;
    };
  }, [hash]);

  if (data === null) return <div className="node-detail-note">Loading…</div>;
  if (data === "unavailable") return <div className="node-detail-note">The bytes aren't available for this packet right now.</div>;
  if (!data.available) return <div className="node-detail-note">{data.note || "No raw bytes were captured for this packet."}</div>;
  return <PacketBytesView key={hash} data={data} routeHops={routeHops} />;
}

// The coloured bytes for packet bytes already in hand (also used by the Decode tool, which passes `caption`
// because its bytes were pasted, not heard). Hover and pin state live here and reset when the view is re-keyed.
export function PacketBytesView({ data, routeHops, caption }: { data: PacketBytesResponse; routeHops: number; caption?: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);
  const legendRef = useRef<HTMLUListElement>(null);
  const fromBytes = useRef(false); // true when the lit field came from the bytes, so the key follows it

  // When a byte is pointed at, keep its key row in view inside the key's own scroll box (never scrolls the page,
  // and never moves rows under a pointer that is on the key itself).
  const lit = activeField(hover, pinned);
  useEffect(() => {
    const box = legendRef.current;
    const row = lit != null && box ? (box.children[lit] as HTMLElement | undefined) : undefined;
    if (!box || !row || !fromBytes.current) return;
    if (row.offsetTop < box.scrollTop) box.scrollTop = row.offsetTop;
    else if (row.offsetTop + row.offsetHeight > box.scrollTop + box.clientHeight) box.scrollTop = row.offsetTop + row.offsetHeight - box.clientHeight;
  }, [lit]);

  const rows = byteRows(data.hex, data.fields);
  const active = lit;
  const activeInfo = active != null ? data.fields[active] : null;
  const groups = groupsPresent(data.fields);
  const hopLine = hopNote(data.path_hops, routeHops);

  return (
    <div className="pb">
      <div className="pb-source">{caption ?? captionText(data.length, data.source, data.observer)}</div>
      {hopLine && <div className="node-detail-note">{hopLine}</div>}
      {data.note && <div className="node-detail-note">{data.note}</div>}

      <div className="pb-grid" role="group" aria-label="Packet bytes">
        {rows.map((row) => (
          <div className="pb-row" key={row.offset}>
            <span className="pb-offset">{row.offset}</span>
            <span className="pb-cells">
              {row.cells.map((c) => {
                const f = c.field >= 0 ? data.fields[c.field] : null;
                const cls = [
                  "pb-byte",
                  `pb-g-${f ? f.group : "other"}`,
                  f && f.start === c.index ? "pb-start" : "",
                  c.field >= 0 && c.field === active ? "pb-active" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <span
                    key={c.index}
                    className={cls}
                    title={f ? fieldSummary(f) : undefined}
                    onMouseEnter={() => {
                      fromBytes.current = true;
                      if (c.field >= 0) setHover(c.field);
                    }}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => {
                      fromBytes.current = true;
                      if (c.field >= 0) setPinned(togglePinned(pinned, c.field));
                    }}
                  >
                    {c.hex}
                  </span>
                );
              })}
            </span>
          </div>
        ))}
      </div>

      <div className="pb-readout" aria-live="polite">
        {activeInfo ? fieldSummary(activeInfo) : "Hover over a byte or a row in the key to see what it is. Tap to hold it."}
      </div>

      <div className="pb-groups">
        {groups.map((g) => (
          <span key={g} className={`pb-group-chip pb-g-${g}`} title={GROUP_INFO[g].hint}>
            {GROUP_INFO[g].label}
          </span>
        ))}
      </div>

      <ul className="pb-legend" ref={legendRef}>
        {data.fields.map((f, i) => (
          <li
            key={`${f.start}-${f.name}`}
            tabIndex={0}
            className={`pb-legend-row${i === active ? " pb-active" : ""}`}
            onMouseEnter={() => {
              fromBytes.current = false;
              setHover(i);
            }}
            onMouseLeave={() => setHover(null)}
            onFocus={() => {
              fromBytes.current = false;
              setHover(i);
            }}
            onBlur={() => setHover(null)}
            onClick={() => {
              fromBytes.current = false;
              setPinned(togglePinned(pinned, i));
            }}
          >
            <span className={`pb-swatch pb-g-${f.group}`} aria-hidden="true" />
            <span className="pb-legend-name">{f.name}</span>
            <span className="pb-legend-range">{rangeText(f)}</span>
            {f.value && <span className="pb-legend-value">{f.value}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
